import { CloseOutlined } from '@ant-design/icons';
import { Button, Chip } from '@mm/components';
import { ENTER_KEY_NAME, TAB_KEY_NAME } from '@mm/constants/keys-codes';
import { ErrorReportingUtils } from '@mm/plugins';
import { FilterItemOperatorTypes } from '@mm/state/model/AudiencesModel';
import { VariableOption } from '@mm/state/model/VariableOptionsModel';
import classNames from 'classnames';
import { sortBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import { getFormattedDisplayValue } from '../../../editor/features/contacts/components/audienceFilters/helpers';
import { getUpdatedTextWithEmoji } from '../EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../EmojiPicker/EmojiPicker';
import Input from '../Input/Input';
import { TagItem } from '../TagItem/TagItem';
import { AudienceFilterItemInputChildProps } from './AudienceFilterItemInput';

const MUST_BE_SINGLE_VALUES = ['<Blank>'];

export const AudienceFilterItemInputValue = (
  props: AudienceFilterItemInputChildProps,
) => {
  const {
    filter,
    setFilter,
    variableOptions,
    allowMultipleValues,
    className,
    setInputRef,
    fetchingVariableOptions,
    withEmoji = false,
    css,
    chipTagItems,
  } = props;
  const [suggestions, setSuggestions] = useState<VariableOption[]>([]);
  const [additionalSuggestionCount, setAdditionalSuggestionCount] = useState(0);
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    if (!allowMultipleValues) {
      setFilterValue(filter.value as string);
    }
  }, [allowMultipleValues, filter.value]);

  const onRemoveValue = (index: number) => {
    const value = [...filter.value];
    value.splice(index, 1);
    setFilter({ ...filter, value });
  };

  const allSuggestions = useMemo(
    () =>
      filter.operator === FilterItemOperatorTypes.EXISTS
        ? [
            { name: 'true', count: 0 },
            { name: 'false', count: 0 },
          ]
        : variableOptions
        ? variableOptions.variableOptions.filter(
            o =>
              !allowMultipleValues ||
              !(filter.value as string[]).includes(o.name),
          )
        : [],
    [allowMultipleValues, filter, variableOptions],
  );
  const fetchSuggestions = (filterValue: string) => {
    const testForSingleValueOK = (s: VariableOption) =>
      !(!allowMultipleValues && MUST_BE_SINGLE_VALUES.includes(s.name));

    const suggestionLimit = window.innerHeight <= 750 ? 10 : 20;

    const eligibleSuggestions = allSuggestions.filter(testForSingleValueOK);

    if (!filterValue) {
      setSuggestions(eligibleSuggestions.slice(0, suggestionLimit));
      setAdditionalSuggestionCount(
        Math.max(0, eligibleSuggestions.length - suggestionLimit),
      );
      return;
    }

    const prioritizedSuggestions: [number, VariableOption][] = [];
    eligibleSuggestions.forEach(s => {
      const matchIndex = s.name.toLowerCase().indexOf(filterValue);
      if (matchIndex >= 0) {
        prioritizedSuggestions.push([matchIndex, s]);
      }
    });

    setSuggestions(
      sortBy(prioritizedSuggestions, s => s[0])
        .slice(0, suggestionLimit)
        .map(s => s[1]),
    );
    setAdditionalSuggestionCount(
      Math.max(0, prioritizedSuggestions.length - suggestionLimit),
    );
  };
  const setValue = (value: string | string[]) =>
    setFilter({
      ...filter,
      value,
    });

  const onChangeValue = (value: string) => {
    setFilterValue(value);
    if (!variableOptions || allowMultipleValues) return;
    switch (variableOptions.variableDataKind) {
      case 'numeric':
        if (!Number.isNaN(Number(value))) {
          setValue(value);
        }
        break;
      case 'string':
        setValue(filter.esType ? [value] : value);
        break;
      default:
        ErrorReportingUtils.reportError(
          `unexpected variable data kind: ${variableOptions.variableDataKind}`,
        );
        break;
    }
  };

  const onSuggestionSelected = (suggestion: VariableOption) => {
    if (allowMultipleValues) {
      if (MUST_BE_SINGLE_VALUES.includes(suggestion.name)) {
        setValue([suggestion.name]);
      } else if (MUST_BE_SINGLE_VALUES.some(sv => filter.value.includes(sv))) {
        // Can't add to an already single value.
      } else {
        setValue([...filter.value, suggestion.name]);
      }
      setFilterValue('');
      setTimeout(() => {
        try {
          (document.activeElement as any).blur();
        } catch (e) {}
      }, 10);
    } else {
      setValue(suggestion.name);
      setFilterValue(suggestion.name);
    }
  };
  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setAdditionalSuggestionCount(0);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === TAB_KEY_NAME || e.key === ENTER_KEY_NAME) {
      e.preventDefault();
      if (filterValue) {
        onSuggestionSelected({ name: filterValue, count: 0 });
      }
    }
  };

  const insertEmoji = (emoji: string) => {
    const newText = getUpdatedTextWithEmoji(filterValue, emoji);
    setFilterValue(newText);
  };

  if (variableOptions?.variableDataKind === 'date_time') {
    return (
      <div
        className={classNames('audience-filter-item-input__section', {
          [`${className}__section`]: className,
        })}
      >
        <div
          className={classNames('audience-filter-item-input__field-title', {
            [`${className}__field-title`]: className,
          })}
        >
          Value
        </div>
        <div
          className={classNames(
            'audience-filter-item-input__datepicker-container',
            {
              [`${className}__datepicker-container`]: className,
            },
          )}
        >
          <Datetime
            className={classNames('audience-filter-item-input__datepicker', {
              [`${className}__datepicker`]: className,
            })}
            value={filter.value as string}
            timeFormat={false}
            onChange={v =>
              moment.isMoment(v) &&
              setFilter({
                ...filter,
                value: moment(v).format(
                  filter.esType ? 'YYYY-MM-DD' : 'MM/DD/YYYY',
                ),
              })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'audience-filter-item-input__section',
        css?.section,
        {
          [`${className}__section`]: className,
          [`${className}__value-section`]: className,
        },
      )}
    >
      <div
        className={classNames(
          'audience-filter-item-input__field-title',
          css?.fieldTitle,
          {
            [`${className}__field-title`]: className,
          },
        )}
      >
        Value
      </div>
      <Autosuggest
        suggestions={suggestions}
        shouldRenderSuggestions={() => true}
        onSuggestionSelected={(e, { suggestion }) =>
          onSuggestionSelected(suggestion)
        }
        onSuggestionsFetchRequested={({ value }) =>
          fetchSuggestions(value.toLowerCase())
        }
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={s => s.name}
        renderInputComponent={inputProps => (
          <>
            <div
              className={classNames(
                'audience-filter-item-input__with-emoji',
                className,
              )}
            >
              <Input
                {...inputProps}
                className={classNames(inputProps.className, css?.valueInput, {
                  [`${className}__suggest-input`]: className,
                })}
                onKeyDown={e => {
                  onInputKeyDown(e);
                  inputProps.onKeyDown?.(e);
                }}
              />
              {fetchingVariableOptions && (
                <div className="audience-filter-item-input__input-loading-spinner">
                  <i className="fas fa-spinner fa-spin" />
                </div>
              )}

              {withEmoji && (
                <EmojiPicker
                  onEmojiPicked={insertEmoji}
                  className="audience-filter-item-input__emoj-picker"
                />
              )}
              {allowMultipleValues && (
                <Button
                  color="secondary-blue"
                  disabled={!filterValue}
                  css={{
                    container: classNames(
                      css?.valueButton,
                      'audience-filter-item-input__add-button',
                    ),
                  }}
                  onClick={() =>
                    onSuggestionSelected({ name: filterValue, count: 0 })
                  }
                >
                  Add
                </Button>
              )}
            </div>
          </>
        )}
        renderSuggestion={(suggestion, { isHighlighted }) => (
          <div
            className={classNames(
              'audience-filter-item-input__suggestions-item',
              {
                'audience-filter-item-input__suggestions-item--selected':
                  isHighlighted,
                [`${className}__suggestions-item`]: className,
              },
            )}
          >
            <div
              className={classNames(
                'audience-filter-item-input__suggestion-name',
                { [`${className}__suggestion-name`]: className },
              )}
            >
              <span>{getFormattedDisplayValue(suggestion.name)}</span>
            </div>
          </div>
        )}
        inputProps={{
          inputRef: (ref: HTMLInputElement) => {
            if (setInputRef) {
              setInputRef(ref);
            }
          },
          placeholder: !fetchingVariableOptions
            ? allowMultipleValues
              ? 'Press enter to add more values'
              : 'Input value'
            : '',
          value: filterValue.toString(),
          onChange: (e, { newValue }) => {
            if (newValue !== undefined) {
              onChangeValue(newValue);
            }
          },
        }}
        renderSuggestionsContainer={({ containerProps, children }) => (
          <div
            {...containerProps}
            className={classNames(
              containerProps['className'],
              css?.suggestionsContainer,
            )}
          >
            {children}
            {!!additionalSuggestionCount && (
              <div className="audience-filter-item-input__additional-suggestions-info">
                {additionalSuggestionCount} more items; type to filter
              </div>
            )}
          </div>
        )}
      />
      {allowMultipleValues && Array.isArray(filter.value) && (
        <div
          className={classNames(
            'audience-filter-item-input__phrases-cloud',
            css?.valueListing,
            {
              [`${className}__phrases-cloud`]: className,
            },
          )}
        >
          {(filter.value as string[]).map((systemValue, i) =>
            chipTagItems ? (
              <Chip
                className={classNames(css?.valueTagItem, {
                  [`${className}__phrases-cloud-tag-item`]: className,
                })}
                key={`${filter.variableId}-${systemValue}`}
                text={getFormattedDisplayValue(systemValue)}
              />
            ) : (
              <TagItem
                className={classNames(css?.valueTagItem, {
                  [`${className}__phrases-cloud-tag-item`]: className,
                })}
                key={`${filter.variableId}-${systemValue}`}
                text={getFormattedDisplayValue(systemValue)}
                onDelete={() => onRemoveValue(i)}
                customIcon={
                  <CloseOutlined
                    onClick={() => onRemoveValue(i)}
                    className="audience-filter-item-input__close-tag"
                  />
                }
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};
